/**
 * "Customise layout": which figure leads the three cards, and the order of the
 * sections below them. Saved on the
 * user's profile (see useOrdersUiPreferences), so it follows them to any device.
 */

import { useEffect, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, SlidersHorizontal } from 'lucide-react';
import {
  CARD_HEADLINE_LABELS,
  CARD_HEADLINE_OPTIONS,
  moveOrdersSection,
  ORDERS_UI_SECTION_LABELS,
  type CardHeadline,
  type OrdersUiSection,
} from '../../types/ordersUiPreferences';
import type { OrdersUiPreferencesStatus } from '../../hooks/useOrdersUiPreferences';

interface OrdersLayoutPanelProps {
  order: OrdersUiSection[];
  status: OrdersUiPreferencesStatus;
  saveError: string | null;
  onChange: (order: OrdersUiSection[]) => void;
  cardHeadline: CardHeadline;
  onCardHeadlineChange: (headline: CardHeadline) => void;
}

export default function OrdersLayoutPanel({
  order, status, saveError, onChange, cardHeadline, onCardHeadlineChange,
}: OrdersLayoutPanelProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  // Close on a click anywhere outside the button and panel, or on Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);
  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="orders-layout-panel"
        className="inline-flex items-center gap-2 rounded-lg border border-gray-600 bg-gray-800/80 px-3 py-1.5 text-sm text-gray-200 hover:bg-gray-700"
      >
        <SlidersHorizontal className="w-4 h-4" aria-hidden />
        Customise layout
      </button>
      {open && (
        <div
          id="orders-layout-panel"
          className="absolute right-0 z-20 mt-2 w-72 rounded-lg border border-gray-600 bg-gray-900 p-3 shadow-xl"
        >
          <fieldset>
            <legend className="text-xs text-gray-400">Cards lead with</legend>
            <div className="mt-1 inline-flex gap-1" role="radiogroup" aria-label="Cards lead with">
              {CARD_HEADLINE_OPTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  role="radio"
                  aria-checked={cardHeadline === option}
                  onClick={() => onCardHeadlineChange(option)}
                  className={`rounded-md px-2.5 py-1 text-xs font-medium ${
                    cardHeadline === option
                      ? 'bg-indigo-500/20 text-indigo-200 ring-1 ring-indigo-400/40'
                      : 'text-gray-300 hover:bg-gray-700/60'
                  }`}
                >
                  {CARD_HEADLINE_LABELS[option]}
                </button>
              ))}
            </div>
          </fieldset>
          <p className="mt-3 text-xs text-gray-400">
            Put the sections in the order you want. Your choices are saved to your profile.
          </p>
          <ul className="mt-2 space-y-1">
            {order.map((section, i) => (
              <li key={section} className="flex items-center gap-2 text-sm text-gray-100">
                <span className="flex-1">{ORDERS_UI_SECTION_LABELS[section]}</span>
                <button
                  type="button"
                  aria-label={`Move ${ORDERS_UI_SECTION_LABELS[section]} up`}
                  disabled={i === 0}
                  onClick={() => onChange(moveOrdersSection(order, section, -1))}
                  className="rounded p-1 text-gray-300 hover:bg-gray-700 disabled:opacity-30 disabled:hover:bg-transparent"
                >
                  <ArrowUp className="w-4 h-4" aria-hidden />
                </button>
                <button
                  type="button"
                  aria-label={`Move ${ORDERS_UI_SECTION_LABELS[section]} down`}
                  disabled={i === order.length - 1}
                  onClick={() => onChange(moveOrdersSection(order, section, 1))}
                  className="rounded p-1 text-gray-300 hover:bg-gray-700 disabled:opacity-30 disabled:hover:bg-transparent"
                >
                  <ArrowDown className="w-4 h-4" aria-hidden />
                </button>
              </li>
            ))}
          </ul>
          {status === 'error' && (
            <p className="mt-2 text-xs text-amber-300">
              Your saved layout could not be loaded, so the standard one is showing.
            </p>
          )}
          {saveError && <p className="mt-2 text-xs text-amber-300" role="status">{saveError}</p>}
        </div>
      )}
    </div>
  );
}
